// ABOUTME: Magic Edit overlay component for AI-assisted text editing.
// ABOUTME: Tracks text selection, shows prompt input, and displays AI suggestions with animations.

import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, RotateCcw, Send, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { OleumIcon } from '@/components/icons/OleumIcon';

interface TextSelection {
  sectionId: string;
  startOffset: number;
  endOffset: number;
  selectedText: string;
  rect?: DOMRect;
}

interface MagicEditOverlayProps {
  selection: TextSelection | null;
  /** Whether Magic Edit is actively engaged (user clicked activation button) */
  isActive: boolean;
  onClear: () => void;
  /** Called when user clicks the activation button to start Magic Edit */
  onActivate: () => void;
  onSubmit: (prompt: string) => Promise<void>;
  suggestion: string | null;
  isLoading: boolean;
  onApply: () => void;
  onReject: () => void;
}

// Animated thinking dots
function ThinkingDots() {
  return (
    <div className="flex items-center gap-1">
      {[0, 1, 2].map((i) => (
        <motion.div
          key={i}
          className="w-1.5 h-1.5 rounded-full bg-primary-600"
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.5, 1, 0.5],
          }}
          transition={{
            duration: 0.8,
            repeat: Infinity,
            delay: i * 0.15,
          }}
        />
      ))}
    </div>
  );
}

// Animated sparkle effect
function SparkleEffect() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {[...Array(6)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-1 h-1 bg-primary-600 rounded-full"
          initial={{
            x: '50%',
            y: '50%',
            scale: 0,
            opacity: 1,
          }}
          animate={{
            x: `${20 + Math.random() * 60}%`,
            y: `${20 + Math.random() * 60}%`,
            scale: [0, 1, 0],
            opacity: [1, 0.8, 0],
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            delay: i * 0.3,
            ease: 'easeOut',
          }}
        />
      ))}
    </div>
  );
}

// Character limits for truncation
const INACTIVE_CHAR_LIMIT = 30;
const ACTIVE_CHAR_LIMIT = 100;

export function MagicEditOverlay({
  selection,
  isActive,
  onClear,
  onActivate,
  onSubmit,
  suggestion,
  isLoading,
  onApply,
  onReject,
}: MagicEditOverlayProps) {
  const [prompt, setPrompt] = useState('');
  const [isTextExpanded, setIsTextExpanded] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Focus the input only when Magic Edit becomes active (not just on selection)
    if (isActive && selection && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isActive, selection]);

  useEffect(() => {
    // Reset prompt and expanded state when selection changes or when deactivated
    if (!selection || !isActive) {
      setPrompt('');
      setIsTextExpanded(false);
    }
  }, [selection, isActive]);

  // Reset expanded state when selection changes
  useEffect(() => {
    setIsTextExpanded(false);
  }, [selection?.sectionId, selection?.startOffset, selection?.endOffset]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!prompt.trim() || isLoading) return;
    await onSubmit(prompt);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClear();
    } else if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (prompt.trim() && !isLoading) {
        onSubmit(prompt);
      }
    }
  };

  // Don't show anything if there's no selection
  if (!selection) return null;

  // Mode 1: Selection exists but Magic Edit not activated - show small activation button
  if (!isActive && !isLoading && !suggestion) {
    const isTruncated = selection.selectedText.length > INACTIVE_CHAR_LIMIT;
    const displayText = isTextExpanded
      ? selection.selectedText
      : selection.selectedText.slice(0, INACTIVE_CHAR_LIMIT);

    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, y: 5, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="fixed bottom-10 left-1/2 -translate-x-1/2 glass-panel backdrop-blur-xl rounded-xl px-4 py-3 shadow-xl z-50 border border-obsidian-border"
        >
          <div className="flex items-center gap-3">
            <div className="p-1.5 rounded-lg bg-obsidian-accent-indigo/20">
              <OleumIcon className="w-4 h-4 text-obsidian-accent-indigo" />
            </div>
            <div className="flex items-center gap-1">
              <span className={`text-xs text-obsidian-text-tertiary ${isTextExpanded ? 'max-w-[400px]' : 'max-w-[200px]'} ${isTextExpanded ? '' : 'truncate'}`}>
                "{displayText}
                {!isTextExpanded && isTruncated ? '...' : ''}"
              </span>
              {isTruncated && (
                <button
                  onClick={() => setIsTextExpanded(!isTextExpanded)}
                  className="p-0.5 hover:bg-white/10 rounded transition-colors flex-shrink-0"
                  title={isTextExpanded ? 'Show less' : 'Show full text'}
                >
                  {isTextExpanded ? (
                    <ChevronUp className="w-3 h-3 text-obsidian-text-tertiary hover:text-white" />
                  ) : (
                    <ChevronDown className="w-3 h-3 text-obsidian-text-tertiary hover:text-white" />
                  )}
                </button>
              )}
            </div>
            <Button
              variant="primary"
              size="sm"
              onClick={onActivate}
              leftIcon={<OleumIcon className="w-3.5 h-3.5" />}
            >
              Ask AI to edit
            </Button>
            <button
              onClick={onClear}
              className="p-1 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-4 h-4 text-obsidian-text-tertiary hover:text-white" />
            </button>
          </div>
        </motion.div>
      </AnimatePresence>
    );
  }

  // Mode 2: Magic Edit is active - show full prompt input / suggestion UI

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 10, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="fixed bottom-10 left-1/2 -translate-x-1/2 w-[520px] bg-surface-0/80 backdrop-blur-xl border border-primary-600/30 rounded-sm p-4 shadow-modal z-50"
      >
        {/* Header */}
        <div className="flex items-center gap-3 mb-3">
          <motion.div
            className="p-2 rounded-sm bg-primary-50 relative"
            animate={isLoading ? { scale: [1, 1.1, 1] } : {}}
            transition={{ duration: 1, repeat: isLoading ? Infinity : 0 }}
          >
            {isLoading ? (
              <OleumIcon className="w-4 h-4 text-primary-600" />
            ) : (
              <OleumIcon className="w-4 h-4 text-primary-600" />
            )}
          </motion.div>
          <span className="text-surface-800 font-medium text-sm">Magic Edit</span>
          {!isLoading && (
            <div className="flex items-center gap-1 flex-1 min-w-0">
              <span className={`text-xs text-surface-500 ${isTextExpanded ? 'max-w-none whitespace-normal' : 'truncate max-w-[350px]'}`}>
                "{isTextExpanded ? selection.selectedText : selection.selectedText.slice(0, ACTIVE_CHAR_LIMIT)}
                {!isTextExpanded && selection.selectedText.length > ACTIVE_CHAR_LIMIT ? '...' : ''}"
              </span>
              {selection.selectedText.length > ACTIVE_CHAR_LIMIT && (
                <button
                  onClick={() => setIsTextExpanded(!isTextExpanded)}
                  className="p-0.5 hover:bg-surface-100 rounded transition-colors flex-shrink-0"
                  title={isTextExpanded ? 'Show less' : 'Show full text'}
                >
                  {isTextExpanded ? (
                    <ChevronUp className="w-3 h-3 text-surface-500 hover:text-surface-800" />
                  ) : (
                    <ChevronDown className="w-3 h-3 text-surface-500 hover:text-surface-800" />
                  )}
                </button>
              )}
            </div>
          )}
          {isLoading && (
            <span className="text-xs text-primary-600 flex items-center gap-2">
              AI is thinking
              <ThinkingDots />
            </span>
          )}
          <button
            onClick={onClear}
            className="ml-auto p-1 hover:bg-surface-100 rounded-sm transition-colors cursor-pointer"
            disabled={isLoading}
          >
            <X className="w-4 h-4 text-surface-500 hover:text-surface-800" />
          </button>
        </div>

        {/* Loading State */}
        {isLoading && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="relative"
          >
            <div className="bg-primary-50 rounded-sm p-6 border border-primary-200 relative overflow-hidden">
              <SparkleEffect />
              <div className="flex flex-col items-center gap-3">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                >
                  <OleumIcon className="w-8 h-8 text-primary-600" />
                </motion.div>
                <div className="text-center">
                  <p className="text-sm text-surface-800 font-medium">
                    Rewriting your text...
                  </p>
                  <p className="text-xs text-surface-500 mt-1">
                    "{prompt}"
                  </p>
                </div>
                {/* Progress bar animation */}
                <div className="w-full h-1 bg-surface-200 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-gradient-to-r from-primary-600 to-accent-600"
                    initial={{ x: '-100%' }}
                    animate={{ x: '100%' }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      ease: 'easeInOut',
                    }}
                    style={{ width: '50%' }}
                  />
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Prompt Input */}
        {!isLoading && suggestion === null && (
          <form onSubmit={handleSubmit}>
            <div className="relative">
              <input
                ref={inputRef}
                type="text"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="How should I rewrite this? (e.g. 'Make it more concise', 'Add more detail')"
                className="w-full bg-surface-0 border border-surface-200 rounded-sm py-3 pl-4 pr-12 text-surface-800 text-sm focus:outline-none focus:border-primary-600 transition-colors placeholder:text-surface-400"
              />
              <button
                type="submit"
                disabled={!prompt.trim()}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-sm bg-primary-600 text-white disabled:opacity-50 hover:bg-primary-700 transition-colors cursor-pointer"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </form>
        )}

        {/* Suggestion Display */}
        {!isLoading && suggestion !== null && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-3"
          >
            {/* Suggestion Preview with diff-like display */}
            <div className="space-y-2">
              {/* Original - struck through */}
              <div className="bg-error-light rounded-sm p-3 border border-error/20">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] font-medium text-error uppercase tracking-wider">
                    Original
                  </span>
                </div>
                <p className="text-sm text-surface-600 line-through opacity-70">
                  {selection.selectedText}
                </p>
              </div>

              {/* Suggestion - highlighted */}
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-success/10 rounded-sm p-3 border border-success/20"
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] font-medium text-success uppercase tracking-wider">
                    Suggested
                  </span>
                  <Check className="w-3 h-3 text-success" />
                </div>
                <p className="text-sm text-surface-800 leading-relaxed">
                  {suggestion}
                </p>
              </motion.div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2 pt-2">
              <Button
                onClick={onApply}
                variant="primary"
                size="sm"
                leftIcon={<Check className="w-4 h-4" />}
              >
                Apply Change
              </Button>
              <Button
                onClick={onReject}
                variant="ghost"
                size="sm"
                leftIcon={<RotateCcw className="w-4 h-4" />}
              >
                Try Again
              </Button>
              <Button
                onClick={onClear}
                variant="ghost"
                size="sm"
                className="ml-auto"
              >
                Cancel
              </Button>
            </div>
          </motion.div>
        )}

        {/* Quick Prompts */}
        {!isLoading && suggestion === null && (
          <div className="flex flex-wrap gap-2 mt-3">
            {['Make it concise', 'Add more detail', 'Make it professional', 'Simplify'].map(
              (quickPrompt) => (
                <button
                  key={quickPrompt}
                  onClick={() => setPrompt(quickPrompt)}
                  className="px-3 py-1.5 text-xs bg-surface-50 hover:bg-surface-100 text-surface-600 hover:text-surface-800 rounded-sm transition-colors cursor-pointer"
                >
                  {quickPrompt}
                </button>
              )
            )}
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
